import React from 'react';
import styled, { x, useTheme, th, up, css } from '@xstyled/styled-components';
import Highlight, { defaultProps } from 'prism-react-renderer';
import {
  LiveProvider,
  LiveEditor,
  LiveError,
  LivePreview as BaseLivePreview
} from 'react-live';
import { mdx } from '@mdx-js/react';

const Pre = styled.pre`
  font-size: 15;
  line-height: 1.45;
  word-break: normal;
  overflow: auto;
  direction: ltr;
  text-align: left;
  white-space: pre;
  word-spacing: normal;
  word-break: normal;
  margin: 4 0;
  background-color: editor-background;
  color: editor-on;
  direction: ltr;
  text-align: left;
  white-space: pre;
  word-spacing: normal;
  word-break: normal;
  hyphens: none;
  padding: 4 0;
  border-left: ${th.space(5)} solid transparent;
  border-right: ${th.space(5)} solid transparent;
  color: inherit;
  border-radius: base;
  box-shadow: 0 0 0 1px #7b99a1;

  textarea {
    &:focus {
      outline: none;
    }
  }
`;

const LivePreview = styled(BaseLivePreview)`
  padding: preview-padding-y preview-padding-x;
  margin: 3 -3 -3;
  border-top: 1;
  border-color: editor-border;
  border-image: initial;

  white-space: normal;
  font-family: base;
  overflow: hidden;

  background-color: background;
  color: on-background;

  ${up(
    'sm',
    css`
      border-right: 1;
      border-left: 1;
      border-radius: editor;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      border-color: editor-border;
      margin-left: -2;
      margin-right: -2;
    `
  )}
`;

const globalModules = {
  react: 'React'
};

export function LiveConfig({ modules }) {
  Object.assign(globalModules, modules);
  return null;
}

function req(path) {
  const dep = globalModules[path];

  if (!dep) {
    throw new Error(
      `Unable to resolve path to module '${path}'. Use "LiveConfig" to provide modules.`
    );
  }
  return dep;
}

function importToRequire(code) {
  return (
    code
      // { a as b } => { a: b }
      .replace(/([0-9a-z_$]+) as ([0-9a-z_$]+)/gi, '$1: $2')
      // import { a } from "a" => const { a } = require("b")
      .replace(
        /import {([^}]+)} from ([^\s;]+);?/g,
        'const {$1} = require($2);'
      )
      // import a from "a" => const a = require("a").default || require("a")
      .replace(
        /import ([\S]+) from ([^\s;]+);?/g,
        'const $1 = require($2).default || require($2);'
      )
      // import * as a from "a"
      .replace(
        /import \* as ([\S]+) from ([^\s;]+);?/g,
        'const $1 = require($2);'
      )
      // import a from "a" => const a = require("a").default || require("a")
      .replace(
        /import (.+),\s?{([^}]+)} from ([^\s;]+);?/g,
        [
          'const $1 = require($3).default || require($3);',
          'const {$2} = require($3);'
        ].join('\n')
      )
  );
}

export function usePrismTheme() {
  const theme = useTheme();
  return th('prism-theme')({ theme });
}

export function Code({ children, lang = 'markup', live, noInline }) {
  const prismTheme = usePrismTheme();

  if (live) {
    return (
      <LiveProvider
        code={children.trim()}
        transformCode={code => `/* @jsx mdx */ ${importToRequire(code)}`}
        scope={{ mdx, require: req }}
        language={lang}
        theme={prismTheme}
        noInline={noInline}
      >
        <LivePreview />
        <Pre
          as="div"
          style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
        >
          <LiveEditor padding={0} />
        </Pre>
        <LiveError />
      </LiveProvider>
    );
  }

  const handleCopy = event => {
    event.preventDefault();
    navigator.clipboard.writeText(children.trim());
  };

  return (
    <>
      <Highlight
        {...defaultProps}
        code={children.trim()}
        language={lang}
        theme={prismTheme}
      >
        {({ className, style, tokens, getLineProps, getTokenProps }) => (
          <Pre className={className} style={style}>
            {tokens.map((line, i) => (
              <div {...getLineProps({ line, key: i })}>
                {line.map((token, key) => (
                  <span {...getTokenProps({ token, key })} />
                ))}
              </div>
            ))}
          </Pre>
        )}
      </Highlight>
      <x.div display="flex" justifyContent="flex-end" mb="4">
        <x.button
          onClick={handleCopy}
          bg="transparent"
          display="flex"
          css={{
            '&:focus': {
              outline: 'none'
            }
          }}
        >
          <svg
            width="13"
            height="16"
            viewBox="0 0 13 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M9.66658 0.666626H1.66659C0.933252 0.666626 0.333252 1.26663 0.333252 1.99996V11.3333H1.66659V1.99996H9.66658V0.666626ZM11.6666 3.33329H4.33325C3.59992 3.33329 2.99992 3.93329 2.99992 4.66663V14C2.99992 14.7333 3.59992 15.3333 4.33325 15.3333H11.6666C12.3999 15.3333 12.9999 14.7333 12.9999 14V4.66663C12.9999 3.93329 12.3999 3.33329 11.6666 3.33329ZM4.33325 14H11.6666V4.66663H4.33325V14Z"
              fill="#4CA48A"
            />
          </svg>{' '}
          <x.span pl="2" fontFamily="Cabin">
            Copy Code
          </x.span>
        </x.button>
      </x.div>
    </>
  );
}
